/**
 * Profile photo resolver — Iter-223b Batch A item #1.
 *
 * Single source of truth used by admin/client/booking/discovery/invoice/staffing
 * views so a talent's avatar renders consistently everywhere. Apply the same
 * fallback chain rather than each page picking a different field.
 *
 * Resolution order:
 *   1. profile_photo_url        (talent-owned headshot)
 *   2. professional_headshot_url (Iter-220+ AI talent / model card)
 *   3. portfolio_images[0]      (first uploaded portfolio image)
 *   4. logo_url                 (client/company logo)
 *   5. avatar_url               (generic avatar field — admin profiles, etc.)
 *   6. null                     (caller renders initials/placeholder)
 *
 * Accepts a flat record OR a profile object with nested portfolio_images.
 * Tolerates both snake_case and camelCase field names.
 */
export const resolveProfilePhoto = (record) => {
  if (!record || typeof record !== 'object') return null;

  // Direct headshot fields (most common)
  const direct = record.profile_photo_url
    || record.profilePhotoUrl
    || record.profile_photo
    || record.professional_headshot_url
    || record.professionalHeadshotUrl
    || record.headshot_url
    || record.headshotUrl;
  if (direct) return direct;

  // Portfolio fallback
  const portfolio = record.portfolio_images || record.portfolioImages;
  if (Array.isArray(portfolio) && portfolio.length > 0) {
    const first = portfolio[0];
    if (typeof first === 'string' && first) return first;
    if (first && typeof first === 'object' && (first.url || first.image_url)) {
      return first.url || first.image_url;
    }
  }

  // Client/company + generic
  return record.logo_url
    || record.logoUrl
    || record.avatar_url
    || record.avatarUrl
    || record.photo_url
    || null;
};

/**
 * Compute display initials for the placeholder when no photo resolves.
 * Falls back through first+last → display_name → company → email.
 */
export const resolveInitials = (record) => {
  if (!record) return '?';
  const first = (record.first_name || record.firstName || '').trim();
  const last = (record.last_name || record.lastName || '').trim();
  if (first || last) return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
  const display = (record.display_name || record.displayName || record.full_name || record.fullName || '').trim();
  if (display) {
    const parts = display.split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || display[0].toUpperCase();
  }
  const company = (record.company_name || record.companyName || record.client_company || '').trim();
  if (company) return company[0].toUpperCase();
  const email = (record.email || '').trim();
  return email[0]?.toUpperCase() || '?';
};

export default resolveProfilePhoto;
